import { ChangeDetectorRef, Component, ElementRef, SimpleChanges, ViewChild } from "@angular/core";
import { BpcChartType, FilterType, WidgetFilter, WidgetTypes } from "@core/models/analytics-dashboard-enums";
import { WidgetConfig } from "@core/models/analytics-dashboard-models";
import { GridsterConfig } from "angular-gridster2";
import { DeviceDetectorService } from "ngx-device-detector";
import * as Highcharts from "highcharts";
import { DEFAULT_BAR_CHART_OPTIONS, DEFAULT_PIE_CHART_OPTIONS } from "@core/models/analytics-dashboard-highcharts";
import { ElevationReportService } from "src/app/services/elevation-report/elevation-report.service";
import { ColumnMapping, DataFilter } from "@core/components/dynamic-table/dynamic-table.component";
import { Subscription, takeUntil } from "rxjs";
import { CardItemMapping } from "@core/components/dynamic-cards/dynamic-cards.component";
import { TabUtilsService } from "src/app/services/tab-utils.service";
import { CompaniesSearchService } from "src/app/services/companies-search/companies-search.service";
import { CompanyV2Service } from "@core/services/company-v2.service";
import { lastn } from "@core/models/analytics-dashboard-constants";
import { InvestorTab } from "@core/models/types";
import { RouterService } from "src/app/services/router/router.service";
import { AnalyticsWidgetService } from "src/app/services/analytics-widget/analytics-widget.service";

export type NoiticleBreakin = {
  companyName: string,
  subject: string,
  outreachOwner: string,
  elevatedBy: string,
  date: Date
}

export type BreakInMember = {
  userName?: string;
  userEmail?: string;
  totalCount?: number;
  totalBreakIns?: number;
  profileImageUrl?: string;
  companyIds?: number[];
}

export type ElevationMember = {
  fullName?: string;
  totalBreakInElevations?: number;
  totalCompaniesElevated?: number;
  profilePictureUrl?: string;
  breakInElevationsCompanyIds? : number[];
  elevatedCompaniesIds?: number[];
  companyIds?: number[];
}

export type ElevationStatusCounts = {
  [statusId: string]: {
    count: number;
    companyIds: number[];
  };
}

@Component({
  selector: "bpc-elevation-report",
  templateUrl: "./elevation-report.component.html",
  styleUrls: ["./elevation-report.component.scss"],
})
export class ElevationReportComponent {
  isLoading = false;
  showTable: boolean = false;
  WidgetTypes = WidgetTypes;
  options: GridsterConfig = {};
  widgets: Array<WidgetConfig> = [];
  notableBreakIns!: NoiticleBreakin[];
  companies: CardItemMapping[] | any = [];
  totalCount = 0;

  elevationsColumns: ColumnMapping[] = [
    { header: "Company", showHeader: false, mappedTo: "nameColumn", case: "company", sortable: true, sortProperty: "name", sortDirection: "desc" },
    { header: "Outreach Owner	", showHeader: true, mappedTo: "outreachOwnerName",},
    { header: "HC / 1Y / 2Y", showHeader: true, mappedTo: "hC1Y2YColumn",},
    { header: "PIC", showHeader: true, mappedTo: "totalPICColumn",},
    //{ header: "Coverage", showHeader: true, mappedTo: "coverageUsersList", editable: true, editAction: "editCoverage"},
    { header: "Investors", showHeader: true, mappedTo: "investorsList",},
    { header: "Financials", showHeader: true, mappedTo: "targetFinancialsColumn",},
    { header: "Next Steps", showHeader: true, mappedTo: "nextStepColumn",}
  ];

  companiesFilter: DataFilter = {
    pageNumber: 1,
    pageSize: 100,
    sortDirection: "asc",
    sortProperty: "name",
    isMyElevation: false,
  };

  filters: WidgetFilter[] = [{ filterType: FilterType.last_n, options: lastn, default: lastn[1] }];
  
  @ViewChild('grid') targetElement!: ElementRef;

  get series(): any{
    return this.widgets[0]?.chartOptions?.series![0];
  }

  private refreshDataSubscription!: Subscription;

  get isMobile(): boolean {
    return this.deviceDetectorService.isMobile();
  }

  constructor(
    private deviceDetectorService: DeviceDetectorService,
    private tabUtilsService: TabUtilsService,
    private companiesSearchService: CompaniesSearchService,
    private companyV2Service: CompanyV2Service,
    private routerService: RouterService,
    private analyticsWidgetService: AnalyticsWidgetService,
  ) {
    this.options = {
      draggable: { enabled: false },
      resizable: { enabled: false },
      gridType: "fit",
      margin: 10,
      maxCols: 9,
      //minRows: 20,
      minItemCols: 2,
      minItemRows: 2,
    };
  }
  


  childTabs: any[] = [
    { label: "Weekly Elevations", route: "" },
    { label: "All Elevations", route: "all" },
    { label: "My Elevations", route: "my" },
    { label: "Stats", route: "report", selected: true },
    { label: "Auto Elevations(alpha)", route: "auto", class: "grey-tab" },
  ];

  initializeWidgets() {
    this.widgets = [
      {
        x: 0,
        y: 0,
        rows: 7,
        cols: 3,
        type: WidgetTypes.elevationOutcomes,
        chartType: BpcChartType.PieChart,
        title: "Firmwide Elevation Outcomes",
        filters: [],
        loaders: 14,
        length: 0
      },
      {
        x: 3,
        y: 0,
        rows: this.isMobile ? 10 : 7,
        cols: 3,
        type: WidgetTypes.seniorTeamBreakIns,
        chartType: BpcChartType.BarChart,
        title: "Elevations",
        filters: [],
        loaders: 18,
        length: 0
      },
      {
        x: 6,
        y: 0,
        rows: this.isMobile ? 10 : 7,
        cols: 3,
        type: WidgetTypes.juniorTeamBreakIns,
        chartType: BpcChartType.BarChart,
        title: "Break Ins",
        filters: [],
        loaders: 18,
        length: 0
      },
      // {
      //   x: 6,
      //   y: 0,
      //   rows: this.isMobile ? 3 : 7,
      //   cols: 3,
      //   type: WidgetTypes.juniorTeamBreakIns,
      //   chartType: BpcChartType.BarChart,
      //   title: "Junior Team Break-Ins",
      //   loaders: 14,
      //   length: 0
      // },
      // {
      //   x: 0,
      //   y: 6,
      //   rows: 4,
      //   cols: 9,
      //   type: WidgetTypes.notableBreakIns,
      //   chartType: BpcChartType.PieChart,
      //   title: "Notable Break-Ins",
      //   loaders: 14,
      // },
    ];
  }

  ngOnInit() {
    this.routerService.updateCurrentTab(InvestorTab.ELEVATIONS);
    this.refreshDataSubscription = this.tabUtilsService.refreshData.subscribe(() => {
      if (this.showTable) this.fetchCompanies();
    });
    this.initializeWidgets();
  }

  onFilterChange(filters: any) {
    this.companies = [];
    this.companiesFilter = { ...this.companiesFilter, ...filters };
    if (filters.pageNumber) {
      this.companiesFilter.pageNumber = filters.pageNumber;
    } else {
      this.companiesFilter.pageNumber = 1;
    }
    this.fetchCompanies();
  }

  fetchCompanies(refreshData = true) {
    if (this.isLoading){
      return;
    }
    this.isLoading = true;

    this.companiesSearchService.getElevationsV2(this.companiesFilter)
      .pipe(takeUntil(this.routerService.destroy$))
      .subscribe({
      next: (response) => {
        if (response?.data?.data) {
          this.tabUtilsService.currentTotal = response?.data.totalCount;
          const newCompanies = this.companyV2Service.transformElevationsData(response.data.data, this.analyticsWidgetService.breakInCompanyIds);
          const element = document.getElementById("grid");
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            if (this.deviceDetectorService.isTablet()) {
              setTimeout(() => {
                scrollTo(0, -500);
              }, 500);
            }
          }
          if (refreshData) {
            this.companies = [...newCompanies];
          } else this.companies.push(...newCompanies);

          this.totalCount = response.data.totalCount;
        }
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        if (this.companiesFilter.pageNumber) this.companiesFilter.pageNumber = this.companiesFilter.pageNumber - 1;
      },
      complete: () => {
      },
    });
  }
  
  showGrid(event: any){
    this.showTable = true;
    this.companiesFilter.companiesInclude = event.companyIds;
    this.companiesFilter.pageNumber = 1;
    this.fetchCompanies();
  }

  hideGrid(event: any){
    this.companies = [];
    this.showTable = false;
  }

  ngOnDestroy(): void {
    if (this.refreshDataSubscription) {
      this.refreshDataSubscription.unsubscribe();
    }
  }

}
